import { useContext } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { resolveEffectsStyle } from "../runtime/effects-resolver"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"
import { useStacksChildren } from "./stacked-children"
import { TEXT_SIZE, TEXT_VARIANT, FONT_WEIGHT, TRACKING, pxToTextSize, resolveColor } from "../lib/style-resolver"
import {
  resolveStyleTokens,
  resolveStyleTokenValue,
  type RendererDesignTokens,
} from "../lib/style-token-resolver"
import {
  isSourceDerivedElement,
  readSourceFrame,
  sourceInlineStyle,
  sourceTextBoxStyle,
} from "./source-derived-rendering"
import {
  ParentActivationHostContext,
  ParentActivationProxy,
  normalizeParentActivationHostTag,
  parentActivationHostStyle,
  PARENT_ACTIVATION_HOST_CLASSES,
  resolveParentActivationLabel,
} from "./parent-activation"

const FONT_WEIGHT_CLASSES: Record<string, string> = {
  ...FONT_WEIGHT,
  w100: "font-thin",
  w200: "font-extralight",
  w300: "font-light",
  w400: "font-normal",
  w500: "font-medium",
  w600: "font-semibold",
  w700: "font-bold",
  w800: "font-extrabold",
  w900: "font-black",
  thin: "font-thin",
  extralight: "font-extralight",
  light: "font-light",
  black: "font-black",
}

/**
 * Text atoms render as `<span>` / `<div>` / heading tags. Because none of the
 * plain-text tags are in the publication visual proof's semantic-descendant
 * rescue list (`input,button,h1,h2,h3,label,p,[role=status]`), a text atom
 * addressed through the boxless `display: contents` wrapper was measured at
 * 0×0 and could never satisfy the visual axis. TextRenderer therefore owns its
 * identity: `props.identity` is spread onto the real text box on every return
 * path (via `dataAttrs`), and `ElementRenderer` emits no wrapper for it.
 */
export function TextRenderer({ element, identity }: RendererProps) {
  const { resolveSymbol, dispatchEvent, hasEventBinding, itemContext, mode, designTokens } = useRenderContext()
  // Inside a composite activation host the host disables its own pointer
  // events, so raised text controls need an inline pointer-events escape.
  // Standalone text actions must not carry one (source-derived contract).
  const insideActivationHost = useContext(ParentActivationHostContext)
  // Theme-v2 atom defaults: surfaced on data-* attrs so the fallback chain
  // can be asserted externally. Note: text.variant is a semantic typography
  // namespace (heading-xl, body, caption) distinct from shadcn theme variants;
  // the existing TEXT_VARIANT className logic below is unchanged.
  const atomDefaults = useAtomDefaults("text")
  const resolvedVariant = (element as { variant?: string }).variant ?? atomDefaults.defaultVariant ?? "default"
  const resolvedSize = (element as { size?: string }).size ?? atomDefaults.defaultSize ?? "default"
  // Support both flat format (element.text) and options format (element.options.text)
  const opts = element.options as Record<string, unknown> | undefined
  const rawText = element.text ?? opts?.text ?? ""
  let finalRaw = String(rawText)
  // Pre-resolve @{...} local references before passing to resolveSymbol
  if (finalRaw.includes("@{")) {
    finalRaw = finalRaw.replace(/@\{([^}]+)\}/g, (_, inner) => {
      const path = (inner as string).trim()
      if (path.startsWith("!")) {
        const val = resolveSymbol(`#{${path.slice(1)}}`)
        return String(!val)
      }
      const val = resolveSymbol(`#{${path}}`)
      return val != null ? String(val) : ""
    })
  }
  const resolved = resolveSymbol(finalRaw)
  const text = resolved == null ? "" : typeof resolved === "object" ? JSON.stringify(resolved, null, 2) : String(resolved)
  // Check if we have children to render (rehype pipeline creates nested text nodes)
  const hasChildren = element.children && element.children.length > 0
  const sourceOptions = element.options as Record<string, unknown> | undefined
  const isSourceText = isSourceDerivedElement(element)
  const hasSourceFrame = isSourceText && readSourceFrame(element) != null
  const sourceTag =
    typeof sourceOptions?.sourceTag === "string"
      ? sourceOptions.sourceTag
      : typeof (element as unknown as { sourceTag?: unknown }).sourceTag === "string"
        ? (element as unknown as { sourceTag: string }).sourceTag
        : undefined

  // Resolve typography from options/typography objects or canonical flat properties.
  const elementRecord = element as unknown as Record<string, unknown>
  const optionsTypography = opts?.typography as Record<string, unknown> | undefined
  const typography = (elementRecord.typography as Record<string, unknown> | undefined) ?? optionsTypography

  // Resolve numeric sizes from options (e.g., 40 → "3xl", 18 → "lg")
  // After normalizeElement(), options.size (number) becomes element.size (string like "64")
  const flatFontSize = finiteNumber(elementRecord.fontSize)
  const rawSize = element.size ?? opts?.size ?? typography?.fontSize
  const numericSize = typeof rawSize === "number" ? rawSize : typeof rawSize === "string" && /^\d+$/.test(rawSize) ? parseInt(rawSize, 10) : null
  const sizeToken = numericSize != null ? pxToTextSize(numericSize) : rawSize
  const rawWeight = elementRecord.fontWeight ?? element.weight ?? opts?.weight ?? typography?.fontWeight
  const rawColor = element.color ?? opts?.color ?? typography?.color
  // Resolve color bindings (e.g., "#{theme.colors.foreground}")
  const colorSource = typeof rawColor === "string" && rawColor.includes("#{")
    ? resolveSymbol(rawColor)
    : rawColor
  const resolvedColor = resolveStyleTokenValue(colorSource, designTokens, "color")

  const colorStr = resolvedColor ? String(resolvedColor) : ""
  const { className: colorClass, style: colorStyle } = resolveColor(colorStr || undefined)

  // Detect if className already has Tailwind classes that handle specific properties.
  // Bridge (rehype) pipeline generates BOTH className (original Tailwind) AND structured options
  // (lineHeight, color, etc.). Applying both causes double-application conflicts — e.g.,
  // lineHeight: 28 (px value from text-xl) rendered as unitless CSS → 28 × font-size = 560px!
  const cls = element.className ?? ""
  const clsHandlesLineHeight = /\b(leading-|text-(xs|sm|base|lg|xl|2xl|3xl|4xl|5xl|6xl|7xl|8xl|9xl)\b)/.test(cls)
  const clsHandlesColor =
    /\btext-(?!xs\b|sm\b|base\b|lg\b|xl\b|2xl\b|3xl\b|4xl\b|5xl\b|6xl\b|7xl\b|8xl\b|9xl\b|center\b|start\b|end\b|left\b|right\b|justify\b|wrap\b|balance\b|pretty\b|ellipsis\b|clip\b|nowrap\b)\S/.test(
      cls,
    )
  const clsHandlesAlign = /\btext-(center|start|end|left|right|justify)\b/.test(cls)
  const clsHandlesTracking = /\btracking-/.test(cls)
  const clsHandlesFontSize = /(?:^|\s)text-(?:xs|sm|base|lg|xl|[2-9]xl|\[[^\]]+\])(?:\s|$)/.test(cls)

  const classes = cn(
    element.variant && (TEXT_VARIANT[element.variant] ?? ""),
    sizeToken ? (TEXT_SIZE[String(sizeToken)] ?? "") : undefined,
    rawWeight ? (FONT_WEIGHT_CLASSES[String(rawWeight)] ?? "") : undefined,
    // Skip colorClass when className already has Tailwind color utility (avoids duplication)
    !clsHandlesColor ? colorClass : undefined,
    element.tracking && (TRACKING[element.tracking] ?? ""),
    element.lineClamp && `line-clamp-${element.lineClamp}`,
    element.margin,
    element.className,
  )

  // Only apply inline color when className doesn't already handle it via Tailwind color utilities
  const inlineStyle: React.CSSProperties = clsHandlesColor ? {} : { ...colorStyle }
  if (flatFontSize != null && !clsHandlesFontSize) {
    inlineStyle.fontSize = `${flatFontSize}px`
  }
  const letterSpacing = elementRecord.letterSpacing ?? opts?.letterSpacing ?? typography?.letterSpacing
  if (letterSpacing != null && !clsHandlesTracking) {
    const resolvedLetterSpacing = resolveTypographyDimension(letterSpacing, designTokens, "letterSpacing")
    if (resolvedLetterSpacing != null) inlineStyle.letterSpacing = resolvedLetterSpacing
  }
  const align = elementRecord.textAlignment ?? elementRecord.align ?? opts?.textAlignment ?? opts?.align ?? typography?.textAlignment
  if (align && !clsHandlesAlign) {
    inlineStyle.textAlign = align as React.CSSProperties["textAlign"]
  }
  // lineHeight from TailwindClassParser is in pixels (e.g., 28 from text-xl's 1.75rem line-height).
  // Applied as unitless CSS, the browser interprets it as a multiplier (28 × font-size = 560px!).
  // When className already has leading-* or text-* with implicit line-height, skip entirely.
  // Otherwise, add px suffix for values that are clearly pixel values (>= 4).
  const lineHeight = elementRecord.lineHeight ?? opts?.lineHeight ?? typography?.lineHeight
  if (lineHeight != null && !clsHandlesLineHeight) {
    const resolvedLineHeight = resolveTypographyDimension(lineHeight, designTokens, "lineHeight")
    if (resolvedLineHeight != null) inlineStyle.lineHeight = resolvedLineHeight
  }
  // Apply opacity from styling
  const styling = element.styling as Record<string, unknown> | undefined
  if (styling?.opacity != null) {
    inlineStyle.opacity = Number(styling.opacity)
  }

  // Preserve source-derived inline typography/style facts. The schema already
  // allows element.style for all nodes; text previously ignored it, which
  // dropped source TSX inline fontSize/fontWeight/margins during ontology
  // parity runs.
  const passthroughStyle = isSourceText
    ? {}
    : resolveStyleTokens(element.style as React.CSSProperties | undefined, designTokens)
  const sourceTextStyle = isSourceText
    ? hasSourceFrame
      ? sourceTextBoxStyle(element, designTokens)
      : (sourceInlineStyle(element, designTokens) ?? {})
    : {}

  // Apply gradientText effect
  const gradientStyle = element.effects?.gradientText ? resolveEffectsStyle({ gradientText: element.effects.gradientText }) : {}
  const finalStyle = { ...inlineStyle, ...passthroughStyle, ...sourceTextStyle, ...gradientStyle }

  // Use semantic heading elements for heading variants (explicit tag override takes priority)
  const tagMap: Record<string, React.ElementType> = {
    h1: "h1",
    h2: "h2",
    h3: "h3",
    h4: "h4",
    h5: "h5",
    h6: "h6",
    p: "p",
    span: "span",
    div: "div",
    pre: "pre",
    code: "code",
    label: "label",
  }
  // className contains block-level CSS → must render as block element (div/p)
  // Detects: block, text-center, text-right, max-w-*, text-balance, w-full, flex, grid
  const hasBlockClassName = element.className != null && /(^|\s)(block|text-center|text-right|max-w-\S+|text-balance|w-full|flex|grid)(\s|$)/.test(element.className)
  // The regex above only ever sees THIS element's own className, so a text atom
  // whose parent declared `space-y-*` stayed a `<span>` and the parent's
  // margin-top rule did nothing to it -- an entire agent surface rendered as
  // one run-on line (measured live 2026-08-24). The parent is the authority on
  // its own layout, so it says so directly instead of the child guessing from
  // a hand-maintained list of block-ish class names.
  const parentStacksChildren = useStacksChildren()
  const headingLevel = element.type === "heading" && typeof element.level === "number" && Number.isInteger(element.level) && element.level >= 1 && element.level <= 6
    ? element.level
    : 2
  const resolvedTag =
    element.type === "heading"
      ? (`h${headingLevel}` as React.ElementType)
      : sourceTag && tagMap[sourceTag]
      ? tagMap[sourceTag]
      : element.tag && tagMap[element.tag]
        ? tagMap[element.tag]
        : element.variant && tagMap[element.variant] // "h1"~"h6", "p" variants → matching HTML tag
          ? tagMap[element.variant]
          : element.variant?.startsWith("heading-xl")
            ? "h1"
            : element.variant?.startsWith("heading-lg")
              ? "h1"
              : element.variant?.startsWith("heading-md")
                ? "h2"
                : element.variant?.startsWith("heading-sm")
                  ? "h3"
                  : element.variant === "caption"
                    ? "p"
                    : numericSize != null && numericSize >= 48
                      ? "h1"
                      : numericSize != null && numericSize >= 30
                        ? "h2"
                      : numericSize != null && numericSize >= 24
                        ? "h3"
                        : hasBlockClassName || parentStacksChildren
                          ? "div"
                          : "span"
  const SafeTag = hasSourceFrame || hasChildren || shouldPromoteTextTagForChildren(resolvedTag, element.children) ? "div" : resolvedTag

  const hasStyle = Object.keys(finalStyle).length > 0

  // Tap event support: text elements with event bindings become clickable
  const hasTap = !!element.events?.tap || (hasEventBinding?.(element.id, "tap") ?? false)
  // Direct text actions are real native buttons. The overlay proxy is reserved
  // for composite text trees where replacing the host would change layout or
  // produce nested interactive markup.
  const usesActivationProxy = hasTap && hasChildren
  const ActivationTag = usesActivationProxy
    ? normalizeParentActivationHostTag(SafeTag)
    : SafeTag
  const sourcePassiveHitTarget =
    isSourceText && mode !== "edit" && !hasTap
      ? ({ pointerEvents: "none" } as React.CSSProperties)
      : {}
  const handleClick = hasTap
    ? (e: React.MouseEvent) => {
        e.preventDefault()
        if (mode !== "edit") e.stopPropagation()
        dispatchEvent(element.id, "tap", itemContext)
      }
    : undefined
  const dataAttrs = {
    "data-testid": `text-${element.id}`,
    "data-atom-type": "text",
    "data-cogo-source-text": isSourceText ? "true" : undefined,
    "data-variant": resolvedVariant,
    "data-size": resolvedSize,
    ...dataAttributesFor(element),
    // Identity last: the addressing contract owns these keys and must not be
    // shadowed by an authored `dataAttributes` entry of the same name.
    ...identity,
  }
  const clickProps = {
    role: usesActivationProxy ? undefined : element.role,
    "aria-label": usesActivationProxy ? undefined : element.ariaLabel,
    style:
      hasStyle || Object.keys(sourcePassiveHitTarget).length > 0
        ? { ...(hasStyle ? finalStyle : {}), ...sourcePassiveHitTarget }
        : undefined,
  }
  const activationProxy = usesActivationProxy ? (
    <ParentActivationProxy
      elementId={element.id}
      label={resolveParentActivationLabel(element, resolveSymbol)}
      onClick={handleClick!}
    />
  ) : null
  const activationClassName = cn(classes, usesActivationProxy && PARENT_ACTIVATION_HOST_CLASSES)
  const activationStyle = usesActivationProxy ? parentActivationHostStyle(element, clickProps.style) : clickProps.style

  // If no direct text but has children (from rehype pipeline), render children instead
  if (!text && hasChildren) {
    return (
      <ActivationTag
        className={activationClassName}
        {...clickProps}
        style={activationStyle}
        data-cogo-composite-action-host={usesActivationProxy ? "true" : undefined}
        {...dataAttrs}
      >
        {activationProxy}
        {element.children!.map((child, idx) => (
          <ElementRenderer key={child.id ?? `child-${idx}`} element={child} />
        ))}
      </ActivationTag>
    )
  }

  if (hasTap) {
    return (
      <button
        type="button"
        className={classes}
        aria-label={element.ariaLabel}
        style={{
          appearance: "none",
          background: "transparent",
          border: 0,
          padding: 0,
          font: "inherit",
          color: "inherit",
          textAlign: "inherit",
          cursor: "pointer",
          position: "relative",
          zIndex: 2,
          // The inline escape is only needed under a composite host (the host
          // sets pointerEvents: "none"); standalone text actions keep an empty
          // inline pointer-events per the source-derived rendering contract.
          ...(insideActivationHost
            ? { pointerEvents: "auto" as const }
            : {}),
          ...clickProps.style,
        }}
        onClick={handleClick}
        {...dataAttrs}
      >
        {text}
      </button>
    )
  }

  return (
    <ActivationTag className={activationClassName} {...clickProps} style={activationStyle} {...dataAttrs}>
      {text}
    </ActivationTag>
  )
}

// Every TextRenderer return path spreads `dataAttrs` (which carries
// `props.identity`) onto its outermost tag, so the identity always lands on the
// real text box. See `cogoOwnsElementIdentity` in core/element-registry.
TextRenderer.cogoOwnsElementIdentity = true as const

function dataAttributesFor(element: RendererProps["element"]) {
  const raw = (
    element as {
      dataAttributes?: Record<string, string | number | boolean | null | undefined>
    }
  ).dataAttributes
  const attrs: Record<string, string | number | boolean> = {}

  if (!raw) return attrs

  for (const [rawKey, value] of Object.entries(raw)) {
    if (value == null) continue
    const key = rawKey.startsWith("data-") ? rawKey : `data-${rawKey.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`)}`
    attrs[key] = value
  }

  return attrs
}

function finiteNumber(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null
}

function resolveTypographyDimension(
  value: unknown,
  designTokens: RendererDesignTokens | undefined,
  property: "letterSpacing" | "lineHeight",
): string | number | undefined {
  const resolved = resolveStyleTokenValue(value, designTokens, property)
  const numeric =
    typeof resolved === "number" && Number.isFinite(resolved)
      ? resolved
      : typeof resolved === "string" && /^-?(?:\d+\.?\d*|\.\d+)$/.test(resolved.trim())
        ? Number(resolved)
        : null

  if (numeric != null) {
    if (property === "letterSpacing") return `${numeric}px`
    return Math.abs(numeric) >= 4 ? `${numeric}px` : numeric
  }
  return typeof resolved === "string" && resolved.trim() ? resolved : undefined
}

const UNSAFE_TEXT_PARENT_TAGS = new Set(["p", "span", "label", "h1", "h2", "h3", "h4", "h5", "h6", "code", "pre"])
const BLOCK_CHILD_TAGS = new Set([
  "address",
  "article",
  "aside",
  "blockquote",
  "div",
  "dl",
  "fieldset",
  "figcaption",
  "figure",
  "footer",
  "form",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "header",
  "hr",
  "li",
  "main",
  "nav",
  "ol",
  "p",
  "pre",
  "section",
  "table",
  "ul",
])
const INLINE_CHILD_TYPES = new Set(["text", "icon", "badge", "kbd", "avatar", "image"])

function shouldPromoteTextTagForChildren(tag: React.ElementType, children?: RendererProps["element"][]) {
  if (typeof tag !== "string" || !UNSAFE_TEXT_PARENT_TAGS.has(tag)) return false
  if (!children?.length) return false
  return children.some(isBlockLikeTextChild)
}

function isBlockLikeTextChild(child: RendererProps["element"]): boolean {
  const childOptions = child.options as Record<string, unknown> | undefined
  const sourceTag =
    typeof childOptions?.sourceTag === "string"
      ? childOptions.sourceTag
      : typeof (child as unknown as { sourceTag?: unknown }).sourceTag === "string"
        ? (child as unknown as { sourceTag: string }).sourceTag
        : undefined
  const childTag = sourceTag ?? child.htmlTag ?? child.tag
  if (childTag && BLOCK_CHILD_TAGS.has(childTag)) return true

  if (INLINE_CHILD_TYPES.has(child.type)) {
    return child.children?.some(isBlockLikeTextChild) ?? false
  }

  return true
}

// All token maps moved to lib/style-resolver.ts
